import React, { useEffect, useState } from 'react';
import axios from '../axiosConfig';
import Modal from '../components/Modal';
import './NotesTable.css';
import { parseDbDate } from '../utils/dates';

const NotesTable = ({
  contactId,
  contactName,
  accountId,
  accountName,
  locationId,
  locationName,
  jobId,
  jobName
}) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteContent, setNoteContent] = useState('');

  const entityParams = () => {
    if (contactId) return {
      contact: {id: contactId, name: contactName, type: 'contact'}
    };
    if (accountId) return {
      account: {id: accountId, name: accountName, type: 'account'}
    };
    if (locationId) return {
      location: {id: locationId, name: locationName, type: 'location'}
    };
    if (jobId) return {
      job: {id: jobId, name: jobName, type: 'job'}
    };
    return {};
  };

  const fetchNotes = async () => {
    setLoading(true);
    try {
      let queryParam = '';
      if (contactId) queryParam = `?contact_id=${contactId}`;
      else if (accountId) queryParam = `?account_id=${accountId}`;
      else if (locationId) queryParam = `?location_id=${locationId}`;
      else if (jobId) queryParam = `?job_id=${jobId}`;
      
      const res = await axios.get(`/notes/${queryParam}`);
      setNotes(res.data);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [contactId, accountId, locationId, jobId]);

  const openModal = (note = null) => {
    setEditingNote(note);
    setNoteContent(note?.content || '');
    setModalOpen(true);
  };

  const closeModal = () => {
    setEditingNote(null);
    setNoteContent('');
    setModalOpen(false);
  };

  const saveNote = async (e) => {
    e.preventDefault();
    try {
      if (editingNote) {
        // Update note
        await axios.patch(`/notes/${editingNote.id}/`, {
          content: noteContent,
          ...entityParams(), // Include names in update
        });
      } else {
        // Create note
        await axios.post('/notes/', {
          content: noteContent,
          ...entityParams(),
        });
      }

      closeModal();
      fetchNotes();
    } catch (err) {
      console.error('Error saving note:', err);
    }
  };

  const deleteNote = async () => {
    if (!editingNote) return;
    try {
      await axios.delete(`/notes/${editingNote.id}/`);
      closeModal();
      fetchNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  return (
    <div className="notes-container">
      <div className="notes-header">
        <button className="add-note-btn" onClick={() => openModal()}>+ New Note</button>
      </div>

      {loading ? (
        <p>Loading notes...</p>
      ) : notes.length === 0 ? (
        <p>No notes found.</p>
      ) : (
        <div className="notes-list">
          {notes.map((note) => {
            const createdBy = note.created_by || {};
            const creatorName =
              `${createdBy.first_name || ''} ${createdBy.last_name || ''}`.trim() || 'Unknown';
            const createdDate = parseDbDate(note.created_at)?.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit',
            });

            return (
              <div className="note-item" key={note.id}>
                <p className="note-content">{note.content}</p>
                <p className="note-meta">- {creatorName}, {createdDate}</p>
                <button className="edit-btn" onClick={() => openModal(note)}>Edit</button>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingNote ? 'Edit Note' : 'New Note'}
      >
        <form onSubmit={saveNote}>
          <textarea autoComplete="off"
            className="note-textarea"
            rows="6"
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            required
          />

          <div className="modal-footer">
            <button type="submit" className="submit-btn">
              Save
            </button>
            {editingNote && (
              <button
                type="button"
                className="delete-btn"
                onClick={deleteNote}
              >
                Delete
              </button>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default NotesTable;
